import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { AuthService } from '../../../login/auth.service'; // Update the path as necessary

import { ClassToggleService, HeaderComponent } from '@coreui/angular';
import { cifTr, cifUs, cifEs, cifGr, cifDe, cilMenu } from '@coreui/icons';
import { TranslateService } from '@ngx-translate/core';

import { global_user } from '../../../global/global';
import { environment } from '../../../../environments/environment';
import { AssistantService } from '../../../services/assistant.service';
import { TokenStorageService } from '../../../login/token-storage.service';
import { AccountMenuItem, visibleAccountMenu } from '../account-menu';
import { Facet, facetMark } from '../../../utils/facet-mark';
import { BusinessLogoService } from '../../../services/business-logo.service';

@Component({
    selector: 'app-default-header',
    templateUrl: './default-header.component.html',
    styleUrls: ['./default-header.component.scss'],
    standalone: false
})
export class DefaultHeaderComponent extends HeaderComponent {
  globalUser = global_user; // Assign global_user to a local variable for template access
  currentLang: string;
  icons = { cifTr, cifUs, cifEs, cifGr, cifDe, cilMenu };
  @Input() sidebarId: string = "sidebar";
  @Input() isMobile = false;
  /** Unread conversations across the channels, shown on the mobile menu button since the menu itself is closed. */
  @Input() channelUnread = 0;
  @Output() mobileNavRequested = new EventEmitter<void>();
  /** P.A. overlay (T199) — desktop header only (C22). */
  assistantOpen = false;
  /** T203 (V183): false until the backend says the P.A. is switched on. */
  assistantEnabled = false;
  private readonly supportedLanguages =
    environment.supportedLanguages || ['en', 'tr', 'es', 'el', 'de'];
  languageLabels: Record<string, string> = {
    en: 'HEADER.LANGUAGE.ENGLISH',
    tr: 'HEADER.LANGUAGE.TURKISH',
    es: 'HEADER.LANGUAGE.SPANISH',
    el: 'HEADER.LANGUAGE.GREEK',
    de: 'HEADER.LANGUAGE.GERMAN',
  };

  /** C28/C29: the account entries this session may see; same list as the mobile nav. */
  get accountItems(): AccountMenuItem[] {
    return visibleAccountMenu(this.tokenStorage);
  }

  /**
   * C29: in a superuser's business session, that business's mark and the same
   * shades as a gradient for the header's top edge. Null for everyone else.
   */
  get business(): { facets: Facet[]; edge: string } | null {
    const tenantId = this.tokenStorage.isSuperuser() ? this.tokenStorage.tenantId() : null;
    if (!tenantId) return null;
    const facets = facetMark(tenantId);
    return { facets, edge: `linear-gradient(90deg, ${facets.map((f) => f.fill).join(', ')})` };
  }

  /** The business's uploaded logo (profile page); null keeps the text/initial fallback. */
  logoUrl: string | null = null;

  /** Who is working: the login e-mail, else the business account's own e-mail. */
  get accountEmail(): string {
    return this.tokenStorage.accountEmail() || global_user.user.email || '';
  }

  get companyName(): string {
    return global_user.user.company_name || '';
  }

  get accountInitial(): string {
    return (this.accountEmail || this.companyName || '?').charAt(0).toLocaleUpperCase('tr');
  }

  public newMessages = new Array(4)
  public newTasks = new Array(5)
  public newNotifications = new Array(5)

  constructor(
    private authService: AuthService,
    private tokenStorage: TokenStorageService,
    private classToggler: ClassToggleService,
    private translate: TranslateService,
    assistant: AssistantService,
    logos: BusinessLogoService
  ) {
    super();
    if (!this.tokenStorage.isPickerSession()) {
      logos.get().subscribe({
        next: (logo) => (this.logoUrl = logos.imageUrl(logo?.path)),
        error: () => (this.logoUrl = null),
      });
    }
    assistant.status().subscribe({
      next: ({ enabled }) => (this.assistantEnabled = enabled === true),
      error: () => (this.assistantEnabled = false),
    });
    // Ensure TranslateService knows about the supported languages
    this.translate.addLangs(this.supportedLanguages);

    // Get the current language or fall back to the default environment language
    this.currentLang =
      this.translate.currentLang ||
      this.translate.defaultLang ||
      environment.language;

    // Subscribe to language changes
    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = event.lang;
    });
    
  }

  // Method to change language
  changeLanguage(language: string) {
    if (!this.supportedLanguages.includes(language)) {
      return;
    }

    this.currentLang = language;
    this.translate.use(language);
  }

  logout() {
    this.authService.logout(); // Invoke the logout process
  }

  /** The account menu's one action today (C29); AuthService owns the failure path. */
  runAction(action: string): void {
    if (action === 'switch-profile') {
      this.authService.switchProfile();
    }
  }
}
